import { Select } from 'shared/form/components/Select';
import { SelectOption } from 'shared/form/models/SelectOption';
import { Tarjeta } from 'core/tarjeta/models/Tarjeta';
import { useEffect, useMemo } from 'react';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { RegisterOptions, useController } from 'react-hook-form';
import { InputPlaceholder } from 'shared/placeholders';
import { SearchCentroCostoRequest } from 'core/centrocosto/models/SearchCentroCostoRequest';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';

interface CentroCostoSelectorProps {
  label: string;
  name: string;
  registerOptions?: RegisterOptions<any, any>;
  filtros?: SearchCentroCostoRequest;
  reloadToken?: number;
}

export const CentroCostoSelector = ({
  filtros,
  name,
  label,
  registerOptions,
  reloadToken = 0,
}: CentroCostoSelectorProps): JSX.Element => {
  const [centrosCostos, setRequest] = useDataFetcher<SelectOption<CentroCosto>[]>();
  const coreService = useCoreService();
  const {
    field: { value, onChange },
    fieldState: { invalid },
    formState: { isSubmitted },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue: null,
  });

  const filtrosEstables = useMemo(() => {
    return filtros;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filtros)]);

  useEffect(() => {
    setRequest(async () => {
      const response = await coreService.getCostCentres(filtrosEstables || {});
      return response.content?.map((centroCosto) => ({
        label: `${centroCosto.numeroCuenta} - ${centroCosto.nombre}`,
        value: centroCosto,
      }));
    });
  }, [coreService, setRequest, filtrosEstables, reloadToken]);

  const selectedOption = useMemo(
    () => centrosCostos.data?.find((option) => option.value.id === value) || null,
    [centrosCostos.data, value],
  );

  const handleSelection = (option: SelectOption<Tarjeta>) => {
    onChange(option?.value.id || null);
  };

  if (centrosCostos.isLoading) {
    return (
      <div className="mt-3 py-1">
        <InputPlaceholder />
      </div>
    );
  }

  return (
    <Select
      name="centro_costo_selector"
      label={label}
      options={centrosCostos.data}
      value={selectedOption}
      onChange={handleSelection}
      classNames={{
        control: () => {
          if (!isSubmitted) {
            return '';
          }
          return invalid ? 'state-error' : 'state-valid';
        },
      }}
    />
  );
};
